// Copyright © 2026 Alex Bruce. All rights reserved.
import React from "react";
import * as Icon from "../components/OEIcons";

/**
 * The account menu behind the avatar, defined once.
 *
 * There were three copies of this list -- one in ProjectPicker, two in
 * OpenEstimate (the main header and the settings header) -- written separately
 * and drifted apart. The in-project pair rendered OS emoji instead of the icon
 * set, which is why the same menu looked like two different products, and
 * neither offered Presets or Phases. Since phases decide how the Schedule and
 * Cash Flow tabs group, colour and sequence work, the only route to them was to
 * leave the project, go through the picker, and hunt the settings sidebar.
 *
 * Content lives here; each surface maps an id to its own action, because the
 * picker navigates through props and the project navigates through tab state.
 */
export type AccountMenuId =
  | "profile" | "organizations" | "presets" | "phases"
  | "insights" | "directory" | "help" | "shortcuts";

export interface AccountMenuEntry {
  id: AccountMenuId;
  label: string;
  icon: React.ReactNode;
  /** The settings section this opens, where the destination is one. */
  settingsTab?: string;
}

export function accountMenuEntries(opts: {
  surface: "picker" | "project";
  canUseInsights?: boolean;
}): AccountMenuEntry[] {
  const inPicker = opts.surface === "picker";
  return [
    { id: "profile", label: "Profile", icon: <Icon.User size={14}/>, settingsTab: "settings-profile" },
    { id: "organizations", label: "Organizations", icon: <Icon.Building size={14}/>, settingsTab: "settings-team" },
    { id: "presets", label: "Presets", icon: <Icon.Settings size={14}/>, settingsTab: "settings-presets" },
    { id: "phases", label: "Phases", icon: <Icon.List size={14}/>, settingsTab: "settings-phase-groups" },
    // Portfolio Insights is a button on the project list now, beside the
    // projects it summarises. It was here, in a menu built for account
    // settings, and people forgot it existed.
    //
    // Org Directory is a picker-level route with no in-project destination,
    // so it is not offered there rather than offered and broken.
    ...(inPicker
      ? [{ id: "directory" as const, label: "Org Directory", icon: <Icon.Users size={14}/> }]
      : []),
    { id: "help", label: "Help & Guides", icon: <Icon.FileText size={14}/> },
    // Shortcuts only do anything inside a project.
    ...(inPicker
      ? []
      : [{ id: "shortcuts" as const, label: "Keyboard Shortcuts", icon: <Icon.Grid size={14}/> }]),
  ];
}
